import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Languages, Trash2, Plus, GripVertical, Sparkles, Copy } from "lucide-react";
import { HotelManualFaqItem } from "@/hooks/useHotelManualFaq";
import { buildAutoFaqItems, type AutoFaqItem } from "@/utils/hotelFaq";
import type { IterpecHotel, IterpecHotelRoom } from "@/types/iterpec";
import type { HotelManualInfo } from "@/hooks/useHotelManualInfo";
import type { AmenityCategory } from "@/utils/hotelAmenities";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";

interface HotelFAQEditorProps {
  hotelId: string | number;
  hotelName: string;
  hotel?: IterpecHotel | null;
  rooms?: IterpecHotelRoom[];
  manualInfo?: HotelManualInfo | null;
  amenityCategories?: AmenityCategory[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}

const FAQ_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'check-in-out', label: 'Check-in / Check-out' },
  { value: 'location', label: 'Ubicación' },
  { value: 'meals', label: 'Comidas' },
  { value: 'rooms', label: 'Habitaciones' },
  { value: 'policies', label: 'Políticas' },
  { value: 'amenities', label: 'Comodidades' },
  { value: 'services', label: 'Servicios' },
];

export function HotelFAQEditor({
  hotelId,
  hotelName,
  hotel,
  rooms,
  manualInfo,
  amenityCategories,
  open,
  onOpenChange,
  onSaved,
}: HotelFAQEditorProps) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState<string | null>(null);
  const [faqItems, setFaqItems] = useState<HotelManualFaqItem[]>([]);
  const [editingItem, setEditingItem] = useState<Partial<HotelManualFaqItem> | null>(null);
  const [isNewItem, setIsNewItem] = useState(false);

  // Compute the auto-generated FAQ items shown on the hotel page,
  // so admins can see them and use them as a base for manual overrides.
  const autoItems: AutoFaqItem[] = useMemo(() => {
    if (!hotel) return [];
    const cleanName = (hotel.Name || hotelName || "").replace(/\s*\([^)]+\)\s*$/, "").trim();
    const destinationName = [hotel.City, hotel.Country].filter(Boolean).join(", ");
    const roomList = rooms || [];
    const boardTypes = roomList.map((r) => r.BoardDescription).filter(Boolean) as string[];
    const bedTypes = roomList.map((r) => r.BedDescription).filter(Boolean) as string[];
    const amenityNames = (amenityCategories || [])
      .flatMap((c) => c.amenities)
      .map((a) => a.name);
    return buildAutoFaqItems({
      hotelName: cleanName,
      destinationName,
      address: hotel.Address ?? null,
      city: hotel.City ?? null,
      country: hotel.Country ?? null,
      phone: hotel.Phone ?? null,
      amenityNames,
      isPetFriendly: manualInfo?.is_pet_friendly ?? null,
      checkInHour: roomList[0]?.CheckInHour ?? null,
      checkOutHour: roomList[0]?.CheckOutHour ?? null,
      boardTypes,
      bedTypes,
      roomOptionsCount: roomList.length,
      hasRefundable: roomList.some(
        (r) => r.CancellationPolicies && r.CancellationPolicies.length > 0,
      ),
      t,
    });
  }, [hotel, rooms, manualInfo, amenityCategories, hotelName, t]);

  // Fetch existing FAQ items when dialog opens
  useEffect(() => {
    if (open) {
      fetchFaqItems();
    }
  }, [open, hotelId]);


  const fetchFaqItems = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("hotel_manual_faq")
        .select("*")
        .eq("hotel_id", hotelId.toString())
        .order("display_order", { ascending: true });

      if (error) throw error;
      setFaqItems(data || []);
    } catch (error) {
      console.error("Error fetching FAQ items:", error);
      toast({ title: "Error al cargar preguntas", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingItem({
      hotel_id: hotelId.toString(),
      question_es: "",
      question_en: null,
      question_pt: null,
      answer_es: "",
      answer_en: null,
      answer_pt: null,
      category: "general",
      display_order: faqItems.length,
      is_active: true,
    });
    setIsNewItem(true);
  };

  const handleUseAutoAsBase = (auto: AutoFaqItem) => {
    // Map auto category label back to select value (best-effort)
    const catMatch = FAQ_CATEGORIES.find(
      (c) => c.label.toLowerCase() === (auto.category || "").toLowerCase(),
    );
    setEditingItem({
      hotel_id: hotelId.toString(),
      question_es: auto.question,
      question_en: null,
      question_pt: null,
      answer_es: auto.answer,
      answer_en: null,
      answer_pt: null,
      category: catMatch?.value || "general",
      display_order: faqItems.length,
      is_active: true,
    });
    setIsNewItem(true);
  };

  const handleEdit = (item: HotelManualFaqItem) => {
    setEditingItem({ ...item });
    setIsNewItem(false);
  };

  const handleTranslate = async (sourceLang: 'es' | 'pt' | 'en') => {
    if (!editingItem) return;
    
    const sourceQuestion = sourceLang === 'es' ? editingItem.question_es : 
                           sourceLang === 'pt' ? editingItem.question_pt : 
                           editingItem.question_en;
    const sourceAnswer = sourceLang === 'es' ? editingItem.answer_es : 
                         sourceLang === 'pt' ? editingItem.answer_pt : 
                         editingItem.answer_en;
    
    if (!sourceQuestion?.trim() || !sourceAnswer?.trim()) {
      toast({ 
        title: "No hay texto para traducir", 
        variant: "destructive" 
      });
      return;
    }

    const targetLanguages: ('es' | 'pt' | 'en')[] = ['es', 'pt', 'en'].filter(l => l !== sourceLang) as any;
    
    setTranslating(sourceLang);

    try {
      // Translate question
      const { data: qData, error: qError } = await supabase.functions.invoke('translate-review', {
        body: {
          sourceText: sourceQuestion.trim(),
          sourceLanguage: sourceLang,
          targetLanguages,
        },
      });
      if (qError) throw qError;

      // Translate answer
      const { data: aData, error: aError } = await supabase.functions.invoke('translate-review', {
        body: {
          sourceText: sourceAnswer.trim(),
          sourceLanguage: sourceLang,
          targetLanguages,
        },
      });
      if (aError) throw aError;

      const updates: Partial<HotelManualFaqItem> = {};
      
      targetLanguages.forEach(lang => {
        if (lang === 'es') {
          updates.question_es = qData?.translations?.es || editingItem.question_es;
          updates.answer_es = aData?.translations?.es || editingItem.answer_es;
        } else if (lang === 'pt') {
          updates.question_pt = qData?.translations?.pt;
          updates.answer_pt = aData?.translations?.pt;
        } else if (lang === 'en') {
          updates.question_en = qData?.translations?.en;
          updates.answer_en = aData?.translations?.en;
        }
      });

      setEditingItem({ ...editingItem, ...updates });
      toast({ title: "Traducción completada" });
    } catch (error) {
      console.error('Translation error:', error);
      toast({ title: "Error al traducir", variant: "destructive" });
    } finally {
      setTranslating(null);
    }
  };

  const handleSaveItem = async () => {
    if (!editingItem?.question_es?.trim() || !editingItem?.answer_es?.trim()) {
      toast({ title: "Pregunta y respuesta en español son obligatorias", variant: "destructive" });
      return;
    }

    setSaving(true);

    const { data: { session } } = await supabase.auth.getSession();
    
    const itemData = {
      hotel_id: hotelId.toString(),
      question_es: editingItem.question_es.trim(),
      question_en: editingItem.question_en?.trim() || null,
      question_pt: editingItem.question_pt?.trim() || null,
      answer_es: editingItem.answer_es.trim(),
      answer_en: editingItem.answer_en?.trim() || null,
      answer_pt: editingItem.answer_pt?.trim() || null,
      category: editingItem.category || 'general',
      display_order: editingItem.display_order ?? faqItems.length,
      is_active: editingItem.is_active ?? true,
      updated_by: session?.user?.id || null,
    };

    try {
      if (isNewItem) {
        const { error } = await supabase
          .from("hotel_manual_faq")
          .insert({ ...itemData, created_by: session?.user?.id || null });
        if (error) throw error;
        toast({ title: "Pregunta agregada" });
      } else {
        const { error } = await supabase
          .from("hotel_manual_faq")
          .update(itemData)
          .eq("id", editingItem.id);
        if (error) throw error;
        toast({ title: "Pregunta actualizada" });
      }

      setEditingItem(null);
      fetchFaqItems();
      onSaved();
    } catch (error) {
      console.error("Error saving FAQ item:", error);
      toast({ title: "Error al guardar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("¿Está seguro de eliminar esta pregunta?")) return;

    try {
      const { error } = await supabase
        .from("hotel_manual_faq")
        .delete()
        .eq("id", id);
      if (error) throw error;
      toast({ title: "Pregunta eliminada" });
      fetchFaqItems();
      onSaved();
    } catch (error) {
      console.error("Error deleting FAQ item:", error);
      toast({ title: "Error al eliminar", variant: "destructive" });
    }
  };

  const handleToggleActive = async (item: HotelManualFaqItem) => {
    try {
      const { error } = await supabase
        .from("hotel_manual_faq")
        .update({ is_active: !item.is_active })
        .eq("id", item.id);
      if (error) throw error;
      fetchFaqItems();
      onSaved();
    } catch (error) {
      console.error("Error toggling FAQ item:", error);
      toast({ title: "Error al actualizar", variant: "destructive" });
    }
  };

  const getAvailableSourceLanguages = () => {
    if (!editingItem) return [];
    const sources: { lang: 'es' | 'pt' | 'en'; label: string; hasText: boolean }[] = [
      { lang: 'es', label: '🇪🇸 Español', hasText: !!(editingItem.question_es?.trim() && editingItem.answer_es?.trim()) },
      { lang: 'pt', label: '🇧🇷 Português', hasText: !!(editingItem.question_pt?.trim() && editingItem.answer_pt?.trim()) },
      { lang: 'en', label: '🇬🇧 English', hasText: !!(editingItem.question_en?.trim() && editingItem.answer_en?.trim()) },
    ];
    return sources.filter(s => s.hasText);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gestionar FAQ Manual</DialogTitle>
          <p className="text-sm text-muted-foreground">{hotelName}</p>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : editingItem ? (
          // Edit/Create form
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label>Categoría</Label>
                <Select 
                  value={editingItem.category || 'general'}
                  onValueChange={(v) => setEditingItem({ ...editingItem, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FAQ_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-24">
                <Label>Orden</Label>
                <Input 
                  type="number" 
                  value={editingItem.display_order ?? 0}
                  onChange={(e) => setEditingItem({ ...editingItem, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Label>Activo</Label>
                <Switch 
                  checked={editingItem.is_active ?? true}
                  onCheckedChange={(v) => setEditingItem({ ...editingItem, is_active: v })}
                />
              </div>
            </div>

            <Tabs defaultValue="es" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="es">🇪🇸 Español *</TabsTrigger>
                <TabsTrigger value="pt">🇧🇷 Português</TabsTrigger>
                <TabsTrigger value="en">🇬🇧 English</TabsTrigger>
              </TabsList>
              
              <TabsContent value="es" className="space-y-3 mt-3">
                <div>
                  <Label>Pregunta *</Label>
                  <Input 
                    value={editingItem.question_es || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, question_es: e.target.value })}
                    placeholder="¿Cuál es la pregunta?"
                  />
                </div>
                <div>
                  <Label>Respuesta *</Label>
                  <Textarea 
                    value={editingItem.answer_es || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, answer_es: e.target.value })}
                    placeholder="La respuesta a la pregunta..."
                    rows={3}
                  />
                </div>
              </TabsContent>

              <TabsContent value="pt" className="space-y-3 mt-3">
                <div>
                  <Label>Pergunta</Label>
                  <Input 
                    value={editingItem.question_pt || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, question_pt: e.target.value })}
                    placeholder="Qual é a pergunta?"
                  />
                </div>
                <div>
                  <Label>Resposta</Label>
                  <Textarea 
                    value={editingItem.answer_pt || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, answer_pt: e.target.value })}
                    placeholder="A resposta à pergunta..."
                    rows={3}
                  />
                </div>
              </TabsContent>

              <TabsContent value="en" className="space-y-3 mt-3">
                <div>
                  <Label>Question</Label>
                  <Input 
                    value={editingItem.question_en || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, question_en: e.target.value })}
                    placeholder="What is the question?"
                  />
                </div>
                <div>
                  <Label>Answer</Label>
                  <Textarea 
                    value={editingItem.answer_en || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, answer_en: e.target.value })}
                    placeholder="The answer to the question..."
                    rows={3}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex items-center justify-between pt-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={!!translating || getAvailableSourceLanguages().length === 0}
                    className="gap-2"
                  >
                    {translating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Traduciendo...
                      </>
                    ) : (
                      <>
                        <Languages className="h-4 w-4" />
                        Traducir con IA
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {getAvailableSourceLanguages().map((source) => (
                    <DropdownMenuItem 
                      key={source.lang}
                      onClick={() => handleTranslate(source.lang)}
                    >
                      Traducir desde {source.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditingItem(null)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveItem} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {isNewItem ? "Agregar" : "Guardar"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          // List view
          <div className="py-4">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-muted-foreground">
                {faqItems.length} pregunta(s) manual(es)
              </p>
              <Button onClick={handleAddNew} size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Agregar Pregunta
              </Button>
            </div>

            {faqItems.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No hay preguntas manuales configuradas.</p>
                <p className="text-sm">Las preguntas automáticas seguirán apareciendo.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {faqItems.map((item) => (
                  <div 
                    key={item.id} 
                    className={`flex items-start gap-3 p-3 border rounded-lg ${!item.is_active ? 'opacity-50 bg-muted' : 'bg-card'}`}
                  >
                    <GripVertical className="h-5 w-5 text-muted-foreground mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded">
                          {FAQ_CATEGORIES.find(c => c.value === item.category)?.label || item.category}
                        </span>
                        {!item.is_active && (
                          <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded">
                            Inactivo
                          </span>
                        )}
                      </div>
                      <p className="font-medium truncate">{item.question_es}</p>
                      <p className="text-sm text-muted-foreground line-clamp-1">{item.answer_es}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Switch 
                        checked={item.is_active}
                        onCheckedChange={() => handleToggleActive(item)}
                      />
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(item)}>
                        <Languages className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {autoItems.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold">
                    FAQ automático de la página ({autoItems.length})
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Estas preguntas se generan a partir de los datos del hotel y se
                  muestran en la página. Use "Personalizar" para crear una versión
                  editable como pregunta manual.
                </p>
                <div className="space-y-2">
                  {autoItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start gap-3 p-3 border rounded-lg bg-muted/30"
                    >
                      <Sparkles className="h-4 w-4 text-primary mt-1 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded">
                            {item.category}
                          </span>
                          <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded">
                            Automático
                          </span>
                        </div>
                        <p className="font-medium text-sm">{item.question}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {item.answer}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 flex-shrink-0"
                        onClick={() => handleUseAutoAsBase(item)}
                      >
                        <Copy className="h-3 w-3" />
                        Personalizar
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
